/**
 * useRfidScanner hook.
 * (2026-04-17, Jason Cox) Shared BLE scanner lifecycle used by ManualCheckout,
 * TagItems, and ManageTags.
 *
 * (2026-04-24, Jason Cox) Refactored into a thin shim over
 * RfidScannerContext. The singleton scanner + connection state now live on
 * the context provider so the connection persists across page transitions;
 * this hook just registers the consumer's onTagScanned / onError listeners
 * for the lifetime of the component and exposes the context's state and
 * actions through the original return shape.
 */
import { useEffect, useRef } from 'react';
import { BleDeviceInfo, RfidScanner } from '../utils/rfidScanner';
import { useRfidScannerContext } from '../contexts/RfidScannerContext';

export interface UseRfidScannerOptions {
  /** Called on every scanned EPC. Trim/dedupe in the consumer. */
  onTagScanned: (epc: string) => void;
  /**
   * Initial scan power in dBm. Honored only if the context's current power
   * is still at its default and no device is connected yet; otherwise the
   * already-set power is preserved so a mounted page doesn't silently
   * reset another page's tuning.
   */
  initialPower?: number;
  /** Called with a human-readable error message. Consumer decides how to display. */
  onError?: (message: string) => void;
}

export interface UseRfidScannerResult {
  scanner: RfidScanner | null;
  connected: boolean;
  connecting: boolean;
  deviceInfo: BleDeviceInfo | null;
  statusText: string;
  batteryLevel: number;
  scanPower: number;
  setScanPower: (power: number) => void;
  connect: () => Promise<void>;
  disconnect: () => void;
  trigger: () => Promise<void>;
}

const DEFAULT_POWER = 24;

export function useRfidScanner(options: UseRfidScannerOptions): UseRfidScannerResult {
  const { onTagScanned, initialPower, onError } = options;
  const ctx = useRfidScannerContext();

  // Keep callbacks in refs so subscription setup fires once per mount.
  const onTagScannedRef = useRef(onTagScanned);
  const onErrorRef = useRef(onError);
  useEffect(() => { onTagScannedRef.current = onTagScanned; }, [onTagScanned]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  // Register tag listener for this mount.
  useEffect(() => {
    const unsub = ctx.subscribeTagScanned((epc) => {
      onTagScannedRef.current?.(epc);
    });
    return unsub;
  }, [ctx]);

  // Register error listener for this mount (only if caller passed one).
  useEffect(() => {
    if (!onError) return;
    const unsub = ctx.subscribeError((msg) => {
      onErrorRef.current?.(msg);
    });
    return unsub;
    // We want this to fire once per mount; the ref keeps the callback fresh.
    // eslint-disable-next-line
  }, [ctx, Boolean(onError)]);

  // First-mount initial-power hint: only applied if nothing's touched the
  // power yet and nothing's connected. Prevents one page's mount from
  // overwriting a power the user tuned on another page.
  const appliedInitialRef = useRef(false);
  useEffect(() => {
    if (appliedInitialRef.current) return;
    appliedInitialRef.current = true;
    if (
      typeof initialPower === 'number' &&
      initialPower !== ctx.scanPower &&
      ctx.scanPower === DEFAULT_POWER &&
      !ctx.connected
    ) {
      ctx.setScanPower(initialPower);
    }
  }, [ctx, initialPower]);

  return {
    scanner: ctx.scanner,
    connected: ctx.connected,
    connecting: ctx.connecting,
    deviceInfo: ctx.deviceInfo,
    statusText: ctx.statusText,
    batteryLevel: ctx.batteryLevel,
    scanPower: ctx.scanPower,
    setScanPower: ctx.setScanPower,
    connect: ctx.connect,
    disconnect: ctx.disconnect,
    trigger: ctx.trigger,
  };
}
